import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Formik } from 'formik';
import moment from 'moment';

import { Paper, Grid, Typography, TextField, Tooltip, FormControlLabel, Switch, IconButton, InputAdornment } from '@material-ui/core';
import Loader from 'components/Loader';
import Input from 'components/Input';
import Button from 'components/Button';
import ButtonConfirmation from 'components/ButtonConfirmation';
import WarningMessage from 'components/WarningMessage';
import Modal from 'components/Modal';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faClock, faBan, faPlusCircle, faMinusCircle } from '@fortawesome/free-solid-svg-icons';

import { Close as CloseIcon } from '@material-ui/icons';

import { setToken } from 'services';
import { loadEventConfig, loadEmailConfigurations, upsertEventConfig, upsertEmailConfigurations } from '../store/thunk';

import { hoursAndMinutesToMinutesWithPad } from 'helpers/converters';
import useWindowDimensions from 'helpers/hooks/useWindowDimensions';
import { normalizeClockTime } from 'components/Input/masks';

import { theme } from 'theme/globalStyles';

import { EMAIL_CONFIG_OPTIONS, formatEmailConfigValues } from './resources';

const CopyIcon = require('../../../assets/img/icon_copy.svg');

function renderEventContent(event) {
  const { timeText } = event;

  const [initial, final] = timeText?.split(' - ');

  if(!initial || !final) {
    return <></>;
  }

  const totalTime = hoursAndMinutesToMinutesWithPad(final) - hoursAndMinutesToMinutesWithPad(initial);
  const totalTimeToHours = Math.ceil(totalTime / 60) * 2;

  return(
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10, width: '100%', height: '100%', backgroundColor: '#EFF0FB', borderRadius: 8, padding: 5 }}>
      {Array.from(Array(totalTimeToHours)).map(() => {
        return(
          <div style={{ width: '80%', height: '25px', backgroundColor: '#D6DCEE', borderRadius: 25 }} />
        );
      })}
    </div>
  );
}

export default function SchedulingConfigurations() {
  const dispatch = useDispatch();

  const history = useHistory();

  const { width } = useWindowDimensions();
  const isSm = width < 960;

  const { token } = useSelector(state => state.login);
  const { userId } = useSelector(state => state.profile);
  const { establishmentId } = useSelector(state => state.businessInfo);
  const { isLoading, isEmailConfigLoading, id, mandatoryClient, weeklyConfig, yearlyConfig, emailConfig: emailConfigFromStore } = useSelector(state => state.schedulingConfigurations);

  const [schedulingConfig, setSchedulingConfig] = useState([]);
  const [schedulingConfigYearly, setSchedulingConfigYearly] = useState([]);
  const [schedulingConfigToCalendar, setSchedulingConfigToCalendar] = useState([]);
  const [schedulingMandatoryClient, setSchedulingMandatoryClient] = useState(!!mandatoryClient);
  const [isEmailConfigModalOpen, setIsEmailConfigModalOpen] = useState(false);

  useEffect(() => {
    if(!token) {
      return history.push('/login');
    }

    if(token) {
      setToken(token);
    }
  }, [token]);

  useEffect(() => {
    if(establishmentId) {
      dispatch(loadEventConfig(userId, establishmentId));
    }
  }, []);

  useEffect(() => {
    if(isEmailConfigModalOpen) {
      dispatch(loadEmailConfigurations(userId, establishmentId));
    }
  }, [isEmailConfigModalOpen]);

  useEffect(() => {
    setSchedulingConfig(weeklyConfig);
  }, [weeklyConfig]);

  useEffect(() => {
    setSchedulingConfigYearly(yearlyConfig);
  }, [yearlyConfig]);

  useEffect(() => {
    setSchedulingMandatoryClient(!!+mandatoryClient);
  }, [mandatoryClient]);

  useEffect(() => {
    let availabilities = [];

    for(let i=0; i<schedulingConfig.length; i++) {
      for(let j=0; j<schedulingConfig[i].availability.length; j++) {
        const weekDay = moment().startOf('week').add('days', schedulingConfig[i].dayId).format('YYYY-MM-DD');

        if(weekDay) {
          availabilities.push({
            start: `${weekDay} ${schedulingConfig[i].availability[j].startTime}:00`,
            end: `${weekDay} ${schedulingConfig[i].availability[j].endTime}:00`
          });
        }
      }
    }

    setSchedulingConfigToCalendar(availabilities);
  }, [schedulingConfig]);

  const handleUpdateSchedulingConfig = (day, index, type, value) => {
    const config = schedulingConfig
      ?.find(item => item.day == day)
      ?.availability
      ?.map((item, availabilityIndex) => {
        if(availabilityIndex == index) {
          return {
            ...item,
            [type]: value
          }
        }

        return item;
      });

    const updatedConfig = schedulingConfig
      ?.map(item => {
        if(item.day == day) {
          return {
            ...item,
            availability: config
          }
        }

        return item;
      });

    setSchedulingConfig(updatedConfig);
  }

  const handleAddSchedulingConfig = day => {
    const updatedConfig = schedulingConfig
      ?.map(item => {
        if(item.day == day) {
          const lastAvailability = item.availability[item.availability.length - 1]?.endTime;

          return {
            ...item,
            availability: item.availability.concat({
              startTime: lastAvailability || '00:00',
              endTime: '23:59'
            })
          }
        }

        return item;
      });

    setSchedulingConfig(updatedConfig);
  }

  const handleRemoveSchedulingConfig = day => {
    const updatedConfig = schedulingConfig
      ?.map(item => {
        if(item.day == day) {
          return {
            ...item,
            availability: []
          }
        }

        return item;
      });

    setSchedulingConfig(updatedConfig);
  }

  const handleRemoveSingleSchedulingConfig = (day, index) => {
    const updatedConfig = schedulingConfig
      ?.map(item => {
        if(item.day == day) {
          return {
            ...item,
            availability: item.availability.filter((_, availabilityIndex) => availabilityIndex != index + 1)
          }
        }

        return item;
      });

    setSchedulingConfig(updatedConfig);
  }

  const handleCopySchedulingConfig = index => {
    const updatedConfig = schedulingConfig
      ?.map(item => {
        return {
          ...item,
          availability: schedulingConfig[index].availability
        }
      });

    setSchedulingConfig(updatedConfig);
  }

  const handleAddNewYearlySchedulingConfig = () => {
    const updatedConfig = schedulingConfigYearly?.concat({
      day: moment(schedulingConfigYearly[schedulingConfigYearly.length - 1]?.day, 'YYYY-MM-DD').add(1, 'days'),
      availability: [
        {
          startTime: '00:00',
          endTime: '23:59'
        }
      ]
    });

    setSchedulingConfigYearly(updatedConfig);
  }

  const handleUpdateYearlySchedulingDay = (index, date) => {
    const updatedConfig = schedulingConfigYearly
      ?.map((item, dayIndex) => {
        if(dayIndex == index) {
          return {
            ...item,
            day: date
          }
        }

        return item;
      })
      ?.map(item => {
        return {
          ...item,
          day: moment(item.day, 'YYYY-MM-DD').format('YYYY-MM-DD')
        }
      })
      ?.sort((firstDate, secondDate) => moment.utc(firstDate?.day).diff(moment.utc(secondDate?.day)));

    setSchedulingConfigYearly(updatedConfig);
  }

  const handleUpdateYearlySchedulingConfig = (day, index, type, value) => {
    const config = schedulingConfigYearly
      ?.find(item => item.day == day)
      ?.availability
      ?.map((item, availabilityIndex) => {
        if(availabilityIndex == index) {
          return {
            ...item,
            [type]: value
          }
        }

        return item;
      });

    const updatedConfig = schedulingConfigYearly
      ?.map(item => {
        if(item.day == day) {
          return {
            ...item,
            availability: config
          }
        }

        return item;
      });

    setSchedulingConfigYearly(updatedConfig);
  }

  const handleAddYearlySchedulingConfig = day => {
    const updatedConfig = schedulingConfigYearly
      ?.map(item => {
        if(item.day == day) {
          const lastAvailability = item.availability[item.availability.length - 1]?.endTime;

          return {
            ...item,
            availability: item.availability.concat({
              startTime: lastAvailability || '00:00',
              endTime: '23:59'
            })
          }
        }

        return item;
      });

    setSchedulingConfigYearly(updatedConfig);
  }

  const handleRemoveYearlySchedulingConfig = day => {
    const updatedConfig = schedulingConfigYearly
      ?.map(item => {
        if(item.day == day) {
          return {
            ...item,
            availability: []
          }
        }

        return item;
      });

    setSchedulingConfigYearly(updatedConfig);
  }

  const handleRemoveAllYearlySchedulingConfig = day => {
    const updatedConfig = schedulingConfigYearly?.filter(item => item.day != day);
    setSchedulingConfigYearly(updatedConfig);
  }

  const handleRemoveSingleYearlySchedulingConfig = (day, index) => {
    const updatedConfig = schedulingConfigYearly
      ?.map(item => {
        if(item.day == day) {
          return {
            ...item,
            availability: item.availability.filter((_, availabilityIndex) => availabilityIndex != index + 1)
          }
        }

        return item;
      });

    setSchedulingConfigYearly(updatedConfig);
  }

  const handleWeeklyStartTime = (item, index, type, lastEndTime, endTime, startTime) => {
    if(startTime?.length > 5) {
      return;
    }

    if(+(startTime?.split(':')[0] ?? 0) > 23) {
      return;
    }

    handleUpdateSchedulingConfig(item?.day, index, type, startTime);
  }

  const handleWeeklyEndTime = (item, index, type, endTime, startTime) => {
    if(endTime?.length > 5) {
      return;
    }

    if(+(endTime?.split(':')[0] ?? 0) > 23) {
      return;
    }

    handleUpdateSchedulingConfig(item.day, index, type, endTime);
  }

  const handleYearlyStartTime = (item, index, type, lastEndTime, endTime, startTime) => {
    if(startTime?.length > 5) {
      return;
    }

    if(+(startTime?.split(':')[0] ?? 0) > 23) {
      return;
    }

    handleUpdateYearlySchedulingConfig(item?.day, index, type, startTime);
  }

  const handleYearlyEndTime = (item, index, type, endTime, startTime) => {
    if(endTime?.length > 5) {
      return;
    }

    if(+(endTime?.split(':')[0] ?? 0) > 23) {
      return;
    }

    handleUpdateYearlySchedulingConfig(item.day, index, type, endTime);
  }

  const handleSubmit = () => {
    const params = {
      mandatoryClient: !!schedulingMandatoryClient ? 1 : 0,
      weeklySettings: Object.fromEntries(schedulingConfig.map(item => [item.dayName, [
        ...item.availability.map(availability => {
          return {
            startTime: `${normalizeClockTime(availability.startTime)}:00`,
            endTime: `${normalizeClockTime(availability.endTime)}:00`
          }
        })
      ]])),
      dayOfMonthSettings: Object.fromEntries(schedulingConfigYearly.map(item => [moment(item.day, 'YYYY-MM-DD').format('YYYY-MM-DD'), item.availability.length == 0 ? [{
        deleted_at: moment().format('YYYY-MM-DD HH:mm:ss')
      }] : [
        ...item.availability.map(availability => {
          return {
            startTime: `${normalizeClockTime(availability.startTime)}:00`,
            endTime: `${normalizeClockTime(availability.endTime)}:00`
          }
        })
      ]]))
    }

    dispatch(upsertEventConfig(userId, establishmentId, id, params));
  }

  const handleSaveEmailConfig = values => {
    const params = formatEmailConfigValues(values);

    return dispatch(upsertEmailConfigurations(userId, establishmentId, params))
      .then(wasUpdated => {
        if(wasUpdated) {
          setIsEmailConfigModalOpen(false);
        }
      });
  }

  return(
    <Paper style={{ height: '100%' }}>
      <Grid container style={{ height: '100%' }}>
        <Grid item xs={12} md={5} lg={4} style={{ backgroundColor: '#FCFCFC', height: '100vh', overflowY: 'scroll', padding: 10 }}>
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
            <div style={{ height: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 5 }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <FontAwesomeIcon
                    icon={faCog}
                    size='lg'
                    color='#022A5C'
                  />
                  <Typography color='textSecondary' style={{ marginLeft: 15 }}>
                    Configurações de agendamento
                  </Typography>
                </div>
                <div>
                  <IconButton onClick={() => history.push('/agendamento')}>
                    <CloseIcon />
                  </IconButton>
                </div>
              </div>
              {isLoading ? (
                <div style={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Loader isLoading={isLoading} />
                </div>
              ) : (
                <>
                  <FormControlLabel
                    labelPlacement='end'
                    label={
                      <Typography style={{ color: '#022A5C', borderBottom: '1px solid #E4E7EA' }}>
                        <b>Cliente obrigatório na criação de agendamento</b>
                      </Typography>
                    }
                    control={
                      <Switch
                        name='mandatoryClient'
                        color='primary'
                        value={schedulingMandatoryClient}
                        checked={schedulingMandatoryClient}
                        onChange={event => setSchedulingMandatoryClient(event.target.checked)}
                      />
                    }
                    style={{ margin: 0 }}
                  />
                  <br />
                  <Typography
                    style={{
                      color: '#022A5C',
                      borderBottom: '1px solid #E4E7EA',
                      marginTop: 5,
                      cursor: 'pointer',
                      display: 'inline-block'
                    }}
                    onClick={() => setIsEmailConfigModalOpen(true)}
                  >
                    <b>Configurações de envio de email</b>
                  </Typography>
                  <Typography style={{ color: '#022A5C', borderBottom: '1px solid #E4E7EA', marginTop: 10, marginBottom: 10 }}>
                    <b>Disponibilidade geral</b>
                  </Typography>
                  {schedulingConfig?.map((item, index) => {
                    return (
                      <div style={{ display: 'flex' }}>
                        <div style={{ width: '10%' }}>
                          <Typography style={{ marginTop: 20, textAlign: 'center', color: theme.palette.primary.main }}>
                            <b>{item.day}</b>
                          </Typography>
                        </div>
                        {(+item.availability.length == 0) && (
                          <div style={{ width: '60%', display: 'flex', justifyContent: 'center' }}>
                            <Typography color='textSecondary' style={{ marginTop: 20 }}>Indisponível</Typography>
                          </div>
                        )}
                        {(+item.availability.length > 0) && (
                          <div style={{ width: '60%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            {item.availability.map((time, index) => {
                              return(
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 2fr)', gap: 5, width: isSm ? '100%' : 250 }}>
                                  <Input
                                    style={{ width: isSm ? '85%' : '100%' }}
                                    fontSize='16px'
                                    type='timeEventSchedulingConfig'
                                    name={`availability.${index}.startTime`}
                                    color='#8D8D8D'
                                    value={time.startTime}
                                    onChange={event => {
                                      handleWeeklyStartTime(item, index, 'startTime', item.availability[index - 1]?.endTime, time.endTime, event.target.value);
                                    }}
                                    startAdornment={
                                      <InputAdornment position='start' style={{ marginLeft: isSm ? 10: 0 }}>
                                        <FontAwesomeIcon
                                          color='#1E5168'
                                          size='sx'
                                          icon={faClock}
                                        />
                                      </InputAdornment>
                                    }
                                  />
                                  <Input
                                    style={{ width: isSm ? '85%' : '100%', fontSize: 16 }}
                                    fontSize='16px'
                                    type='timeEventSchedulingConfig'
                                    name={`availability.${index}.endTime`}
                                    color='#8D8D8D'
                                    value={time.endTime}
                                    onChange={event => {
                                      handleWeeklyEndTime(item, index, 'endTime', event.target.value, time.startTime);
                                    }}
                                    startAdornment={
                                      <InputAdornment position='start' style={{ marginLeft: isSm ? 10: 0 }}>
                                        <FontAwesomeIcon
                                          color='#1E5168'
                                          size='sx'
                                          icon={faClock}
                                        />
                                      </InputAdornment>
                                    }
                                  />
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <div style={{ width: '30%', display: 'grid', gridTemplateRows: `repeat(${+item.availability.length}, 2fr)`, justifyContent: 'center' }}>
                          <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: isSm ? 15 : 25 }}>
                            {(+item.availability.length > 0) && (
                              <Tooltip
                                placement='top'
                                title='Indisponível'
                              >
                                <IconButton
                                  style={{ padding: 0 }}
                                  onClick={() => handleRemoveSchedulingConfig(item.day)}
                                >
                                  <FontAwesomeIcon
                                    icon={faBan}
                                    size='xs'
                                    color='#022A5C'
                                  />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip
                              placement='top'
                              title='Adicionar'
                            >
                              <IconButton
                                style={{ padding: 0 }}
                                onClick={() => handleAddSchedulingConfig(item.day)}
                              >
                                <FontAwesomeIcon
                                  icon={faPlusCircle}
                                  size='xs'
                                  color='#022A5C'
                                />
                              </IconButton>
                            </Tooltip>
                            {(+item.availability.length > 0) && (
                              <ButtonConfirmation
                                style={{ padding: 0 }}
                                modalTitle='Replicar disponibilidade'
                                modalText={
                                  <Typography style={{ color: theme.palette.primary.main }}>
                                    Deseja replicar a disponibilidade de horário desse dia para TODOS os outros dias da semana?
                                  </Typography>
                                }
                                modalButtons={[
                                  {
                                    title: 'Confimar',
                                    color: 'success',
                                    onClick: () => handleCopySchedulingConfig(index)
                                  }
                                ]}
                                tooltip='Replicar para todos'
                                iconButton={
                                  <img src={CopyIcon} />
                                }
                              />
                            )}
                          </div>
                          {(+item.availability.length > 1) && (
                            <>
                              {item.availability.slice(1).map((_, index) => {
                                return (
                                  <div style={{ width: '100%', marginTop: 10 }}>
                                    <Tooltip
                                      placement='top'
                                      title='Indisponível'
                                    >
                                      <IconButton
                                        style={{ padding: 0 }}
                                        onClick={() => handleRemoveSingleSchedulingConfig(item.day, index)}
                                      >
                                        <FontAwesomeIcon
                                          icon={faBan}
                                          size='xs'
                                          color='#022A5C'
                                        />
                                      </IconButton>
                                    </Tooltip>
                                  </div>
                                );
                              })}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <Typography style={{ color: '#022A5C', borderBottom: '1px solid #E4E7EA', marginTop: 20, marginBottom: 10 }}>
                    <b>Disponibilidade ajustada</b>
                  </Typography>
                  <WarningMessage
                    textAlign='left'
                    message='Indique os horários em que você está disponível em datas específicas.'
                  />
                  {schedulingConfigYearly?.map((item, index) => {
                    return(
                      <div style={{ display: 'flex' }}>
                        <div style={{ width: isSm ? '35%' : '30%', paddingRight: 5 }}>
                          <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                              inputFormat="DD MMM. YYYY"
                              value={item.day}
                              renderInput={params => (
                                <TextField
                                  {...params}
                                  fullWidth
                                  name="item.day"
                                  variant='outlined'
                                  inputProps={{
                                    ...params.inputProps,
                                    readOnly: true,
                                    style: {
                                      color: '#8D8D8D'
                                    }
                                  }}
                                  SelectProps={{
                                    MenuProps: {
                                      disableEnforceFocus: true
                                    }
                                  }}
                                />
                              )}
                              onChange={date => handleUpdateYearlySchedulingDay(index, moment(date).format('YYYY-MM-DD'))}
                            />
                          </LocalizationProvider>
                        </div>
                        {(+item.availability.length == 0) && (
                          <div style={{ width: '40%', display: 'flex', justifyContent: 'center' }}>
                            <Typography color='textSecondary' style={{ marginTop: 20 }}>Indisponível</Typography>
                          </div>
                        )}
                        {(+item.availability.length > 0) && (
                          <div style={{ width: '40%' }}>
                            {item.availability.map((time, index) => {
                              return(
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 2fr)', gap: 5, width: '80%' }}>
                                  <Input
                                    type='timeEventSchedulingConfig'
                                    name={`availability.${index}.startTime`}
                                    color='#8D8D8D'
                                    value={time.startTime}
                                    onChange={event => {
                                      handleYearlyStartTime(item, index, 'startTime', item.availability[index - 1]?.endTime, time.endTime, event.target.value);
                                    }}
                                  />
                                  <Input
                                    type='timeEventSchedulingConfig'
                                    name={`availability.${index}.endTime`}
                                    color='#8D8D8D'
                                    value={time.endTime}
                                    onChange={event => {
                                      handleYearlyEndTime(item, index, 'endTime', event.target.value, time.startTime);
                                    }}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <div style={{ width: '30%', display: 'grid', gridTemplateRows: `repeat(${+item.availability.length}, 2fr)` }}>
                          <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: isSm ? 15 : 25}}>
                            {(+item.availability.length > 0) && (
                              <Tooltip
                                placement='top'
                                title='Indisponível'
                              >
                                <IconButton
                                  style={{ padding: 0 }}
                                  onClick={() => handleRemoveYearlySchedulingConfig(item.day)}
                                >
                                  <FontAwesomeIcon
                                    icon={faBan}
                                    size='xs'
                                    color='#022A5C'
                                  />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip
                              placement='top'
                              title='Adicionar'
                            >
                              <IconButton
                                style={{ padding: 0 }}
                                onClick={() => handleAddYearlySchedulingConfig(item.day)}
                              >
                                <FontAwesomeIcon
                                  icon={faPlusCircle}
                                  size='xs'
                                  color='#022A5C'
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              placement='top'
                              title='Remover'
                            >
                              <IconButton
                                style={{ padding: 0 }}
                                onClick={() => handleRemoveAllYearlySchedulingConfig(item.day)}
                              >
                                <FontAwesomeIcon
                                  icon={faMinusCircle}
                                  size='xs'
                                  color='#022A5C'
                                />
                              </IconButton>
                            </Tooltip>
                          </div>
                          {(+item.availability.length > 1) && (
                            <>
                              {item.availability.slice(1).map((_, index) => {
                                return(
                                  <div style={{ width: '100%', marginTop: 10 }}>
                                    <Tooltip
                                      placement='top'
                                      title='Indisponível'
                                    >
                                      <IconButton
                                        style={{ padding: 0 }}
                                        onClick={() => handleRemoveSingleYearlySchedulingConfig(item.day, index)}
                                      >
                                        <FontAwesomeIcon
                                          icon={faBan}
                                          size='xs'
                                          color='#022A5C'
                                        />
                                      </IconButton>
                                    </Tooltip>
                                  </div>
                                );
                              })}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div>
                    <Button
                      fullWidth
                      type='button'
                      color='primary'
                      onClick={() => handleAddNewYearlySchedulingConfig()}
                    >
                      Adicionar Disponibilidade
                    </Button>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 10, paddingBottom: 10 }}>
                    <div style={{ width: isSm ? '50%' : 'fit-content' }}>
                      <Button
                        fullWidth={isSm}
                        type='button'
                        color='tertiary'
                        variant='outlined'
                        onClick={() => history.push('/agendamento')}
                      >
                        Fechar
                      </Button>
                    </div>
                    <div style={{ width: isSm ? '50%' : 'fit-content' }}>
                      <Button
                        fullWidth={isSm}
                        type='button'
                        color='primary'
                        onClick={handleSubmit}
                      >
                        Salvar
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </Grid>
        {isLoading ? (
          <Grid item xs={12} md={7} lg={8} style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Loader isLoading={isLoading} />
          </Grid>
        ) : (
          <>
            {!isSm && (
              <Grid item xs={12} md={7} lg={8} style={{ height: '100%' }}>
                <Grid container>
                  <Grid item xs={12} style={{ height: '100%', padding: '0 5px 5px 5px' }}>
                    <FullCalendar
                      locale='pt-br'
                      plugins={[ timeGridPlugin ]}
                      headerToolbar={{
                        start: '',
                        center: '',
                        end: ''
                      }}
                      dayHeaderContent={option => {
                        return(
                          <div>
                            <Typography color='textSecondary' variant='body2'>
                              {moment(option.date).format('ddd').toUpperCase()}
                            </Typography>
                          </div>
                        );
                      }}
                      slotMinTime='00:00'
                      slotMaxTime='23:59'
                      slotLabelContent={option => {
                        return(
                          <Typography color='textSecondary' variant='body2'>
                            {option.text} {+option.text >= 12 ? 'pm' : 'am'}
                          </Typography>
                        );
                      }}
                      events={schedulingConfigToCalendar}
                      eventContent={renderEventContent}
                      allDaySlot={false}
                      expandRows={true}
                      contentHeight='95vh'
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
          </>
        )}
      </Grid>
      {isEmailConfigModalOpen && (
        <Formik
          enableReinitialize
          initialValues={formatEmailConfigValues(emailConfigFromStore)}
          onSubmit={handleSaveEmailConfig}
        >
          {({ values, setFieldValue, handleSubmit }) => (
            <Modal
              id='email-config-modal'
              maxWidth='sm'
              title='Configurações de envio de email'
              open={isEmailConfigModalOpen}
              onClose={() => setIsEmailConfigModalOpen(false)}
              modalActions={
                <>
                  <Button
                    type='button'
                    variant='outlined'
                    color='tertiary'
                    className='mr-3'
                    loading={isEmailConfigLoading}
                    onClick={() => setIsEmailConfigModalOpen(false)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type='button'
                    color='primary'
                    loading={isEmailConfigLoading}
                    onClick={handleSubmit}
                  >
                    Salvar
                  </Button>
                </>
              }
            >
              <Loader isLoading={isEmailConfigLoading}>
                {EMAIL_CONFIG_OPTIONS.map(option => (
                  <FormControlLabel
                    key={option.name}
                    labelPlacement='end'
                    label={
                      <Typography style={{ color: '#022A5C' }}>
                        <b>{option.label}</b>
                      </Typography>
                    }
                    control={
                      <Switch
                        name={option.name}
                        color='primary'
                        checked={!!+values[option.name]}
                        onChange={event => setFieldValue(option.name, event.target.checked ? 1 : 0)}
                      />
                    }
                    style={{ margin: 0, display: 'flex' }}
                  />
                ))}
              </Loader>
            </Modal>
          )}
        </Formik>
      )}
    </Paper>
  );
}